import React, { useState } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaFilePdf,
  FaBook,
  FaClipboardList,
  FaFile,
} from "react-icons/fa";

import { useTournamentDocuments } from "../../../hooks/useTournamentDocuments";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";
import { useTournamentAuth } from "../../../utils/authorization";
// DocumentViewer no longer needed since we open in new tab
import FileUpload from "../../FileUpload/FileUpload";
import { supabase } from "../../../supabaseClient";
// Design Standards imports
import { Card } from "../../UI/DesignStandards/Cards";
import { FormField } from "../../UI/DesignStandards/Forms";
import {
  PrimaryButton,
  SecondaryButton,
  SoftButton,
  IconButton,
} from "../../UI/DesignStandards/Buttons";

const DOCUMENT_TYPES = [
  { value: "flyer", label: "Tournament Flyer", icon: FaFilePdf },
  { value: "rules", label: "Tournament Rules", icon: FaBook },
  { value: "entry_form", label: "Entry Form", icon: FaClipboardList },
  { value: "other", label: "Other", icon: FaFile },
];

// Input sanitization functions
const sanitizeText = (text) => {
  if (!text) return "";
  return text
    .trim()
    .replace(/[<>]/g, "") // Remove < and > to prevent HTML injection
    .replace(/&/g, "&amp;") // Encode ampersands
    .replace(/"/g, "&quot;") // Encode quotes
    .replace(/'/g, "&#x27;") // Encode apostrophes
    .substring(0, 150); // Limit length to 150 characters
};

const sanitizeDescription = (text) => {
  if (!text) return "";
  return text
    .trim()
    .replace(/[<>]/g, "") // Remove < and > to prevent HTML injection
    .replace(/&/g, "&amp;") // Encode ampersands
    .replace(/"/g, "&quot;") // Encode quotes
    .replace(/'/g, "&#x27;") // Encode apostrophes
    .substring(0, 1000); // Limit length for descriptions
};

const validateDocumentType = (type) => {
  const validTypes = DOCUMENT_TYPES.map((t) => t.value);
  return validTypes.includes(type) ? type : "other";
};

const TournamentDocuments = ({ tournamentId }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  // selectedDocument state no longer needed since we open in new tab
  const [uploadedFileData, setUploadedFileData] = useState(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    documentType: "flyer",
    url: "",
    fileName: "",
    isPublic: true,
  });

  const { canManage } = useTournamentAuth(tournamentId);
  const {
    documents,
    loading,
    error,
    addDocument,
    updateDocument,
    deleteDocument,
  } = useTournamentDocuments(tournamentId);
  const [formError, setFormError] = useState("");
  const [actionError, setActionError] = useState("");

  const handleUploadComplete = (fileData) => {
    setUploadedFileData(fileData);
    setFormData((prev) => ({
      ...prev,
      url: fileData.url,
      fileName: fileData.fileName,
    }));
    // UI shows uploaded file details; no toast
  };

  const handleUploadError = (error) => {
    setFormError(typeof error === "string" ? error : "Upload failed");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!uploadedFileData && !editingDocument) {
      setFormError("Please upload a file first");
      return;
    }

    // Sanitize form data
    const sanitizedData = {
      title: sanitizeText(formData.title),
      description: sanitizeDescription(formData.description),
      documentType: validateDocumentType(formData.documentType),
      url: formData.url, // URL is already validated by FileUpload component
      fileName: sanitizeText(formData.fileName),
      isPublic: true, // Always public
    };

    // Validate required fields
    if (!sanitizedData.title.trim()) {
      setFormError("Document title is required");
      return;
    }

    try {
      if (editingDocument) {
        await updateDocument(editingDocument.id, sanitizedData);
        setEditingDocument(null);
      } else {
        await addDocument(sanitizedData);
      }
      setShowAddForm(false);
      resetForm();
    } catch (error) {
      console.error("Error saving document:", error);
      setFormError("Failed to save document");
    }
  };

  const handleEdit = (document) => {
    setEditingDocument(document);
    setFormData({
      title: document.title || "",
      description: document.description || "",
      documentType: validateDocumentType(document.documentType),
      url: document.url || "",
      fileName: document.fileName || "",
      isPublic: true, // Always public for tournament documents
    });
    setUploadedFileData(null); // Clear uploaded file data for editing
    setShowAddForm(true);
  };

  const handleDeleteConfirm = async (documentId) => {
    try {
      // Get the document to find the file path
      const documentToDelete = documents.find((doc) => doc.id === documentId);

      // Delete from database
      await deleteDocument(documentId);

      // Delete file from storage if it's a Supabase Storage URL
      if (documentToDelete && documentToDelete.url.includes("supabase.co")) {
        try {
          // Extract the file path from the URL
          const urlParts = documentToDelete.url.split("/");
          const fileName = urlParts[urlParts.length - 1];
          const filePath = `${tournamentId}/${fileName}`;

          const { error: storageError } = await supabase.storage
            .from("tournament-documents")
            .remove([filePath]);

          if (storageError) {
            console.error("Error deleting file from storage:", storageError);
            // Don't show error to user since document was deleted from DB
          }
        } catch (storageError) {
          console.error("Error deleting file from storage:", storageError);
        }
      }

      // UI reflects deletion; no toast
    } catch (error) {
      console.error("Error deleting document:", error);
      setActionError("Failed to delete document");
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const handleDeleteClick = (documentId) => {
    setDeletingDocumentId(documentId);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      documentType: "flyer",
      url: "",
      fileName: "",
      isPublic: true,
    });
    setUploadedFileData(null);
  };

  const openDocument = (url, title) => {
    // Open document in Google Viewer in a new tab for better UX
    const googleDocsUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
      url
    )}&embedded=true`;
    window.open(googleDocsUrl, "_blank", "noopener,noreferrer");
  };

  const closeDocument = () => {
    // No longer needed since we open in new tab
  };

  const getDocumentTypeIcon = (documentType) => {
    const type = DOCUMENT_TYPES.find((t) => t.value === documentType);
    return type ? type.icon : FaFile;
  };

  if (loading) {
    return (
      <Card className="mb-6">
        <h2 className="heading-display text-xl mb-4">Documents</h2>
        <div className="space-y-3 animate-pulse">
          <div className="h-4 bg-brand-charcoal/10 rounded w-1/2" />
          <div className="h-4 bg-brand-charcoal/10 rounded w-2/3" />
          <div className="h-4 bg-brand-charcoal/10 rounded w-1/3" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mb-6">
        <InlineAlert
          type="error"
          title="Error loading documents"
          message={error}
        />
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="heading-display text-xl">Documents</h2>
          {canManage && (
            <SecondaryButton
              onClick={() => {
                setShowAddForm(true);
                setEditingDocument(null);
                resetForm();
              }}
              icon={FaPlus}
              className="!px-3 !py-2 text-sm"
            >
              <span className="hidden sm:inline">Add Document</span>
              <span className="sm:hidden">Add</span>
            </SecondaryButton>
          )}
        </div>

        {showAddForm && (
          <div className="mb-6 p-4 card-inset">
            <h3 className="heading-display text-lg mb-4">
              {editingDocument ? "Edit Document" : "Add Document"}
            </h3>
            {formError ? (
              <div className="mb-3">
                <InlineAlert
                  type="error"
                  title="Please fix the following"
                  message={formError}
                  onClose={() => setFormError("")}
                />
              </div>
            ) : null}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4">
                <FormField
                  label="Document Title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 150) {
                      setFormData({ ...formData, title: value });
                    }
                  }}
                  required
                  maxLength={150}
                  placeholder="Enter document title"
                  error={
                    formData.title.length >= 150 ? "Maximum length reached" : ""
                  }
                />
                {formData.title.length > 0 && (
                  <div className="flex justify-between items-center -mt-2">
                    <div
                      className={`text-xs ${
                        formData.title.length >= 150
                          ? "text-red-600 font-medium"
                          : formData.title.length >= 120
                          ? "text-orange-600"
                          : "text-brand-muted"
                      }`}
                    >
                      {formData.title.length}/150 characters
                    </div>
                    {formData.title.length >= 120 &&
                      formData.title.length < 150 && (
                        <div className="text-xs text-orange-600 font-medium">
                          Approaching limit
                        </div>
                      )}
                  </div>
                )}

                <FormField
                  label="Document Type"
                  type="select"
                  value={formData.documentType}
                  onChange={(e) =>
                    setFormData({ ...formData, documentType: e.target.value })
                  }
                  required
                >
                  {DOCUMENT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </FormField>
              </div>

              {/* File Upload Section */}
              {!editingDocument && (
                <div>
                  <label className="block text-sm font-medium text-brand-charcoal mb-2">
                    Upload PDF File *
                  </label>
                  <FileUpload
                    tournamentId={tournamentId}
                    onUploadComplete={handleUploadComplete}
                    onUploadError={handleUploadError}
                    maxSize={2 * 1024 * 1024} // 2MB
                  />
                </div>
              )}

              {/* Show current file for editing */}
              {editingDocument && (
                <div>
                  <label className="block text-sm font-medium text-brand-charcoal mb-2">
                    Current File
                  </label>
                  <div className="p-3 card-inset">
                    <div className="flex items-center space-x-2">
                      <FaFilePdf className="text-red-500" />
                      <span className="text-sm text-brand-charcoal">
                        {formData.fileName}
                      </span>
                    </div>
                    <p className="text-xs text-brand-muted mt-1">
                      To change the file, delete this document and create a new
                      one.
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-2">
                  Description
                </label>
                <div className="relative">
                  <textarea
                    value={formData.description}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 1000) {
                        setFormData({ ...formData, description: value });
                      }
                    }}
                    rows={3}
                    maxLength={1000}
                    className={`w-full px-4 py-3 border border-brand-charcoal/20 rounded-lg text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink transition-all duration-200 ${
                      formData.description.length >= 1000
                        ? "border-red-300"
                        : ""
                    }`}
                    placeholder="Enter document description (optional)"
                  />
                  {formData.description.length > 0 && (
                    <div className="flex justify-between items-center mt-1">
                      <div
                        className={`text-xs ${
                          formData.description.length >= 1000
                            ? "text-red-600 font-medium"
                            : formData.description.length >= 800
                            ? "text-orange-600"
                            : "text-brand-muted"
                        }`}
                      >
                        {formData.description.length}/1000 characters
                      </div>
                      {formData.description.length >= 800 &&
                        formData.description.length < 1000 && (
                          <div className="text-xs text-orange-600 font-medium">
                            Approaching limit
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <PrimaryButton
                  type="submit"
                  disabled={!uploadedFileData && !editingDocument}
                >
                  {editingDocument ? "Update Document" : "Add Document"}
                </PrimaryButton>
                <SecondaryButton
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingDocument(null);
                    resetForm();
                  }}
                >
                  Cancel
                </SecondaryButton>
              </div>
            </form>
          </div>
        )}

        {documents.length === 0 ? (
          <div className="text-center py-8 text-brand-muted">
            <FaFile className="text-4xl mx-auto mb-4 text-brand-mist" />
            <p>No documents added yet.</p>
            {canManage && (
              <p className="text-sm mt-2">
                Click "Add Document" to upload tournament documents.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {actionError ? (
              <InlineAlert
                type="error"
                title="There was a problem"
                message={actionError}
                onClose={() => setActionError("")}
                className="mb-2"
              />
            ) : null}
            {documents.map((document, index) => {
              const IconComponent = getDocumentTypeIcon(document.documentType);
              return (
                <div
                  key={document.id}
                  className="flex items-center justify-between p-4 card-inset min-w-0"
                >
                  <div className="flex items-center space-x-4 min-w-0 flex-1">
                    <IconComponent
                      className="text-brand-muted text-xl flex-shrink-0"
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <button
                        onClick={() =>
                          openDocument(document.url, document.title)
                        }
                        className="text-left w-full min-w-0 overflow-hidden text-brand-charcoal hover:underline focus:outline-none focus:ring-2 focus:ring-brand-gold-ink focus:ring-inset"
                        title={`View ${document.title}`}
                        aria-label={`View ${document.title} - ${
                          DOCUMENT_TYPES.find(
                            (t) => t.value === document.documentType
                          )?.label
                        }`}
                      >
                        <div className="font-medium truncate">
                          {document.title}
                        </div>
                        <div className="text-sm text-brand-muted truncate">
                          {
                            DOCUMENT_TYPES.find(
                              (t) => t.value === document.documentType
                            )?.label
                          }
                          {document.description && ` • ${document.description}`}
                        </div>
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                    <IconButton
                      onClick={() => openDocument(document.url, document.title)}
                      icon={FaEye}
                      variant="secondary"
                      size="sm"
                      className="text-brand-charcoal hover:text-brand-muted"
                      title={`View ${document.title}`}
                      aria-label={`View ${document.title} (alternative button)`}
                    />

                    {canManage && (
                      <>
                        <IconButton
                          onClick={() => handleEdit(document)}
                          icon={FaEdit}
                          variant="secondary"
                          size="sm"
                          className="text-brand-muted hover:text-brand-charcoal"
                          title={`Edit ${document.title}`}
                          aria-label={`Edit ${document.title}`}
                        />
                        {deletingDocumentId === document.id ? (
                          <div
                            className="flex items-center space-x-1"
                            role="group"
                            aria-label="Delete confirmation"
                          >
                            <span className="text-xs text-red-600 whitespace-nowrap">
                              Delete?
                            </span>
                            <SoftButton
                              color="red"
                              size="xs"
                              onClick={() => handleDeleteConfirm(document.id)}
                              aria-label={`Confirm delete ${document.title}`}
                            >
                              Yes
                            </SoftButton>
                            <SoftButton
                              color="cream"
                              size="xs"
                              onClick={() => setDeletingDocumentId(null)}
                              aria-label={`Cancel delete ${document.title}`}
                            >
                              No
                            </SoftButton>
                          </div>
                        ) : (
                          <IconButton
                            onClick={() => handleDeleteClick(document.id)}
                            icon={FaTrash}
                            variant="danger"
                            size="sm"
                            title={`Delete ${document.title}`}
                            aria-label={`Delete ${document.title}`}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Document Viewer Modal - No longer needed since we open in new tab */}
    </>
  );
};

export default TournamentDocuments;
